import * as React from 'react';
import { useRef } from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import MultipleSelector from './MultipleSelector';
import useLock from '../hooks/useLock';
const Selector = (props, ref) => {
    const inputRef = useRef(null);
    const compositionStatusRef = useRef(false);
    const { prefixCls, open, mode, showSearch, tokenWithEnter, onInputPaste, onSearch, onSearchSubmit, onToggleOpen, onInputKeyDown, domRef, } = props;
    const multiSelectorRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            var _a;
            (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        blur: () => {
            var _a;
            (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        },
        onKeyDown: e => {
            var _a;
            const { which } = e;
            (_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.onKeyDown(e);
            if (which === KeyCode.LEFT || which === KeyCode.RIGHT || which === KeyCode.BACKSPACE) {
                setTimeout(() => {
                    var _a;
                    (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                });
            }
        },
        getLastEnabledIndex: () => { var _a; return ((_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.getLastEnabledIndex()) || 0; },
        getChosenList: () => { var _a; return ((_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.getChosenList()) || []; },
        setInputIndex: (index) => {
            var _a;
            (_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.setInputIndex(index);
        },
        clearChosenList: () => {
            var _a;
            (_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.clearChosenList();
        },
        setChosenValue: (values) => {
            var _a;
            (_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.setChosenValue(values);
        },
    }));
    const [getInputMouseDown, setInputMouseDown] = useLock(0);
    const onInternalInputKeyDown = event => {
        const { which } = event;
        if (which === KeyCode.UP || which === KeyCode.DOWN) {
            event.preventDefault();
        }
        if (onInputKeyDown) {
            onInputKeyDown(event);
        }
        if (which === KeyCode.ENTER && mode === 'tags' && !compositionStatusRef.current && !open) {
            onSearchSubmit(event.target.value);
        }
        if (![KeyCode.SHIFT, KeyCode.TAB, KeyCode.BACKSPACE, KeyCode.ESC].includes(which)) {
            onToggleOpen(true);
        }
    };
    const onInternalInputMouseDown = () => {
        setInputMouseDown(true);
    };
    const pastedTextRef = useRef(null);
    const triggerOnSearch = (value) => {
        if (onSearch(value, true, compositionStatusRef.current) !== false) {
            onToggleOpen(true);
        }
    };
    const onInputCompositionStart = () => {
        compositionStatusRef.current = true;
    };
    const onInputCompositionEnd = e => {
        compositionStatusRef.current = false;
        if (mode !== 'combobox') {
            triggerOnSearch(e.target.value);
        }
    };
    const onInputChange = event => {
        let { target: { value }, } = event;
        if (tokenWithEnter && pastedTextRef.current && /[\r\n]/.test(pastedTextRef.current)) {
            const replacedText = pastedTextRef.current
                .replace(/[\r\n]+$/, '')
                .replace(/\r\n/g, ' ')
                .replace(/[\r\n]/g, ' ');
            value = value.replace(replacedText, pastedTextRef.current);
        }
        pastedTextRef.current = null;
        triggerOnSearch(value);
    };
    const _onInputPaste = e => {
        const { clipboardData } = e;
        const value = clipboardData.getData('text');
        pastedTextRef.current = value;
    };
    const onClick = e => {
        var _a;
        const { target } = e;
        if (target !== inputRef.current && target === domRef) {
            const isIE = document.body.style.msTouchAction !== undefined;
            if (isIE) {
                setTimeout(() => {
                    var _a;
                    (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                });
            }
            else {
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
    };
    const onMouseDown = event => {
        var _a, _b;
        const { target, details } = event;
        const inputMouseDown = getInputMouseDown();
        if (event.target !== inputRef.current && !inputMouseDown) {
            event.preventDefault();
        }
        if (target === domRef.current) {
            const { offsetX, offsetY: _offsetY } = event.nativeEvent;
            const children = target.querySelectorAll(`.${prefixCls}-selection-item`);
            const offsetY = _offsetY + target.scrollTop;
            let i = 0;
            for (; i < children.length; i += 1) {
                const child = children[i];
                if (!((child.offsetLeft <= offsetX && child.offsetTop <= offsetY && child.offsetTop + child.offsetHeight >= offsetY) ||
                    child.offsetTop + child.offsetHeight <= offsetY)) {
                    break;
                }
            }
            (_a = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _a === void 0 ? void 0 : _a.setInputIndex(children.length - i);
            (_b = multiSelectorRef === null || multiSelectorRef === void 0 ? void 0 : multiSelectorRef.current) === null || _b === void 0 ? void 0 : _b.clearChosenList();
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            });
        }
        if ((mode !== 'combobox' && (!showSearch || !inputMouseDown)) || !open) {
            if (open) {
                onSearch('', true, false);
            }
            onToggleOpen();
        }
    };
    const sharedProps = {
        inputRef,
        onInputKeyDown: onInternalInputKeyDown,
        onInputMouseDown: onInternalInputMouseDown,
        onInputChange,
        onInputPaste: onInputPaste || _onInputPaste,
        onInputCompositionStart,
        onInputCompositionEnd,
    };
    return (React.createElement("div", { ref: domRef, className: `${prefixCls}-selector`, onClick: onClick, onMouseDown: onMouseDown },
        React.createElement(MultipleSelector, Object.assign({ ref: multiSelectorRef }, props, sharedProps))));
};
const ForwardSelector = React.forwardRef(Selector);
ForwardSelector.displayName = 'Selector';
export default ForwardSelector;
